import { CommonModule } from '@angular/common';
import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  Firestore,
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  collectionData,
} from '@angular/fire/firestore';
import { AuthService } from 'src/app/auth.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import { ShiftTimeInputComponent } from '../shift-time-input/shift-time-input.component';
import { saveAs } from 'file-saver';
import { firstValueFrom } from 'rxjs';

interface Employee {
  name: string;
  availability: { [day: string]: string };
  requestedOff: Array<{ date: string; status: string; approvedBy?: string }>;
  fullTime: boolean;
  keyHolder: boolean;
  cashier?: boolean;
  orgId: string;
  role: string;
}

interface ScheduleEntry {
  name: string;
  Sun: string;
  Mon: string;
  Tue: string;
  Wed: string;
  Thu: string;
  Fri: string;
  Sat: string;
  totalHours: number;
  daysOff: number;
}

interface ShiftSlot {
  start: number;
  end: number;
  needsKeyHolder: boolean;
}

interface DayCoverageEmployeeCard {
  entry: ScheduleEntry;
  shift: string;
  startMinutes: number;
  keyHolder: boolean;
  fullTime: boolean;
  cashier: boolean;
}

@Component({
  selector: 'app-upload-schedule',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ShiftTimeInputComponent,
  ],
  templateUrl: './upload-schedule.component.html',
  styleUrl: './upload-schedule.component.css',
})
export class UploadScheduleComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  employeeData: Employee[] | null = null;
  result: ScheduleEntry[] = [];
  loading = false;
  weekStart: string = '';
  weekEnd: string = '';
  employeesList: string[] = [];
  publishWeekStart: string = '';
  publishWeekEnd: string = '';

  savingDraft = false;
  loadingDraft = false;
  publishing = false;
  draftExists = false;
  publishedExists = false;
  lastSaved: Date | null = null;
  lastPublished: Date | null = null;
  selectedRemoveIndex: number | null = null;

  publishedSchedules: any[] = [];
  selectedPublishedSchedule: string = '';
  loadingPublished = false;
  isResultsMaximized = false;

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  orgId: string | null = null;
  currentUser: any = null;
  employeeNotes: Array<{ id: string; name: string; notes: string }> = [];
  loadingNotes = false;

  weekDates: string[] = ['', '', '', '', '', '', ''];
  shiftsCount: {
    [day: string]: {
      openers: number;
      closers: number;
      midShifts: number;
      totalWorkers: number;
    };
  } | null = null;
  selectedCoverageDayHeader = '';
  selectedCoverageEmployees: DayCoverageEmployeeCard[] = [];
  private scheduleGenerationAttempts = 0;
  private scheduleGenerationInProgress = false;

  private readonly DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  private readonly FULL_TIME_MIN_HOURS = 32;
  private readonly FULL_TIME_TARGET_HOURS = 42;
  private readonly PART_TIME_MAX_HOURS = 28;
  private readonly OPENING_TIME_START = 540; // 9:00 AM in minutes
  private readonly OPENING_TIME_END = 660; // 11:00 AM in minutes
  private readonly CLOSING_TIME = 1320; // 10:00 PM in minutes
  private readonly SUNDAY_OPEN = 660; // 11:00 AM
  private readonly SUNDAY_CLOSE = 1110; // 6:30 PM
  private readonly WEEKDAY_OPEN = 540; // 9:00 AM
  private readonly WEEKDAY_CLOSE = 1320; // 10:00 PM
  private readonly MIN_SUNDAY_WORKERS = 5;
  private readonly MIN_WEEKDAY_WORKERS = 7;
  private readonly MAX_WORKERS_PER_DAY = 7;
  private readonly TARGET_OPENERS = 3;
  private readonly TARGET_CLOSERS = 3;
  private readonly TARGET_MID_SHIFTS = 1;

  constructor(
    private firestore: Firestore,
    private auth: AuthService,
    private firebase: FirebaseService,
  ) {}

  async ngOnInit() {
    this.currentUser = this.auth.getUser();
    this.orgId = this.currentUser?.orgId || (await this.auth.getUsersOrgId());

    if (this.orgId) {
      await this.checkDraftExists();
      await this.checkPublishedExists();
      await this.loadPublishedSchedules();
    } else {
      this.showToast('No organization found for user.', 'error');
    }
    await this.loadEmployeesList();
  }

  ngAfterViewInit() {
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    );
    tooltipTriggerList.map((tooltipTriggerEl: any) => {
      return new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
    });
  }

  ngOnDestroy() {
    document.body.classList.remove('results-fullscreen-active');
  }

  toggleResultsMaximize(): void {
    this.isResultsMaximized = !this.isResultsMaximized;
    document.body.classList.toggle(
      'results-fullscreen-active',
      this.isResultsMaximized,
    );
  }

  async loadEmployeesList() {
    if (!this.orgId) {
      console.warn('No orgId found, skipping employee list load.');
      return;
    }

    try {
      const employees = await this.firebase.getEmployeesByOrgId(this.orgId);

      // Filter to only include employees and managers (exclude admin and spectator roles)
      const filteredEmployees = employees.filter((emp: any) => {
        const role = emp.role?.toLowerCase() || '';
        return role === 'employee' || role === 'manager';
      });

      this.employeesList = filteredEmployees
        .map((emp: any) => emp.name || emp.Name)
        .filter((n: string) => !!n)
        .sort((a: string, b: string) => a.localeCompare(b));
    } catch (error) {
      console.error('Error loading employee list:', error);
      this.showToast('Failed to load employees list.', 'error');
    }
  }

  async checkDraftExists() {
    if (!this.orgId) return;

    try {
      const draftRef = doc(this.firestore, 'schedules', `${this.orgId}-draft`);
      const draftSnap = await getDoc(draftRef);
      this.draftExists = draftSnap.exists();

      if (this.draftExists) {
        const data = draftSnap.data();
        this.lastSaved = data?.['lastSaved']?.toDate() || null;
      }
    } catch (error) {
      console.error('Error checking draft:', error);
    }
  }

  async saveDraft() {
    if (!this.result?.length) {
      this.showToast(
        'No schedule to save. Please generate a schedule first.',
        'error',
      );
      return;
    }
    if (!this.orgId) {
      this.showToast('No organization found for user.', 'error');
      return;
    }

    this.savingDraft = true;
    try {
      const draftRef = doc(this.firestore, 'schedules', `${this.orgId}-draft`);
      const draftData = {
        result: this.result,
        weekStart: this.weekStart,
        weekEnd: this.weekEnd,
        employeeData: this.employeeData,
        weekDates: this.weekDates,
        lastSaved: new Date(),
        status: 'draft',
      };

      await setDoc(draftRef, draftData);
      this.lastSaved = new Date();
      this.draftExists = true;
      this.showToast('Draft saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving draft:', error);
      this.showToast('Failed to save draft. Please try again.', 'error');
    } finally {
      this.savingDraft = false;
    }
  }

  async loadDraft() {
    if (!this.orgId) {
      this.showToast('No organization found for user.', 'error');
      return;
    }

    this.loadingDraft = true;
    try {
      const draftRef = doc(this.firestore, 'schedules', `${this.orgId}-draft`);
      const draftSnap = await getDoc(draftRef);

      if (draftSnap.exists()) {
        const data = draftSnap.data();
        this.result = data['result'] || [];
        this.weekStart = data['weekStart'] || '';
        this.weekEnd = data['weekEnd'] || '';
        this.employeeData = data['employeeData'] || null;
        this.weekDates = data['weekDates'] || this.weekDates;
        this.lastSaved = data['lastSaved']?.toDate() || null;

        this.showToast('Draft loaded successfully!', 'success');
      } else {
        this.showToast('No draft found for this organization.', 'error');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      this.showToast('Failed to load draft. Please try again.', 'error');
    } finally {
      this.loadingDraft = false;
    }
  }

  async loadPublishedSchedules() {
    if (!this.orgId) return;

    try {
      const schedulesRef = collection(this.firestore, 'schedules');
      const q = query(
        schedulesRef,
        where('orgId', '==', this.orgId),
        where('status', '==', 'published'),
      );
      const querySnapshot = await getDocs(q);

      this.publishedSchedules = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          weekStart: data['weekStart'],
          weekEnd: data['weekEnd'],
          weekRange: data['weekRange'],
          publishedAt: data['publishedAt']?.toDate() || null,
          label: this.formatWeekLabel(data['weekStart'], data['weekEnd']),
        };
      });

      this.publishedSchedules.sort((a, b) => {
        if (!a.publishedAt || !b.publishedAt) return 0;
        return b.publishedAt.getTime() - a.publishedAt.getTime();
      });
    } catch (error) {
      console.error('Error loading published schedules:', error);
      this.showToast('Failed to load published schedules list.', 'error');
    }
  }

  async loadPublishedSchedule() {
    if (!this.selectedPublishedSchedule) {
      this.showToast('Please select a schedule to load.', 'error');
      return;
    }

    this.loadingPublished = true;
    try {
      const scheduleRef = doc(
        this.firestore,
        'schedules',
        this.selectedPublishedSchedule,
      );
      const scheduleSnap = await getDoc(scheduleRef);

      if (scheduleSnap.exists()) {
        const data = scheduleSnap.data();
        this.result = data['result'] || [];
        this.weekStart = data['weekStart'] || '';
        this.weekEnd = data['weekEnd'] || '';
        this.weekDates = data['dates'] || this.weekDates;

        this.showToast(
          `Published schedule loaded: ${this.formatWeekLabel(
            this.weekStart,
            this.weekEnd,
          )}`,
          'success',
        );
      } else {
        this.showToast('Selected schedule not found.', 'error');
      }
    } catch (error) {
      console.error('Error loading published schedule:', error);
      this.showToast(
        'Failed to load published schedule. Please try again.',
        'error',
      );
    } finally {
      this.loadingPublished = false;
    }
  }

  async checkPublishedExists() {
    if (!this.orgId) return;

    try {
      const schedulesRef = collection(this.firestore, 'schedules');
      const q = query(
        schedulesRef,
        where('orgId', '==', this.orgId),
        where('status', '==', 'published'),
      );
      const querySnapshot = await getDocs(q);

      this.publishedExists = !querySnapshot.empty;

      if (this.publishedExists && !querySnapshot.empty) {
        const schedules = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            publishedAt: data['publishedAt'],
            weekRange: data['weekRange'],
            weekStart: data['weekStart'],
            weekEnd: data['weekEnd'],
          };
        });

        schedules.sort((a, b) => {
          const dateA = a.publishedAt?.toDate?.() || new Date(0);
          const dateB = b.publishedAt?.toDate?.() || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });

        this.lastPublished = schedules[0]?.publishedAt?.toDate?.() || null;
      }
    } catch (error) {
      console.error('Error checking published schedules:', error);
    }
  }

  openPublishModal() {
    this.publishWeekStart = this.weekStart;
    this.publishWeekEnd = this.weekEnd;

    const modalEl = document.getElementById('confirmPublishModal');
    if (modalEl) {
      const modal = new (window as any).bootstrap.Modal(modalEl);
      modal.show();
    }
  }

  public formatWeekRangeForId(start: string, end: string): string {
    const formatDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-');
      return `${month}-${day}`;
    };

    return `${formatDate(start)}-${formatDate(end)}`;
  }

  async confirmPublish() {
    if (!this.result?.length) {
      this.showToast(
        'No schedule to publish. Please generate a schedule first.',
        'error',
      );
      return;
    }

    if (!this.publishWeekStart || !this.publishWeekEnd) {
      this.showToast('Please select both a week start and end date.', 'error');
      return;
    }

    if (new Date(this.publishWeekEnd) < new Date(this.publishWeekStart)) {
      this.showToast('Week end date must be after the start date.', 'error');
      return;
    }

    if (!this.orgId) {
      this.showToast('No organization ID found for this user.', 'error');
      return;
    }

    this.publishing = true;
    try {
      const user = this.auth.getUser();

      const weekRange = this.formatWeekRangeForId(
        this.publishWeekStart,
        this.publishWeekEnd,
      );

      const docId = `${this.orgId}-published-${weekRange}`;
      const publishedRef = doc(this.firestore, 'schedules', docId);

      const publishedData = {
        result: this.result,
        weekStart: this.publishWeekStart,
        weekEnd: this.publishWeekEnd,
        weekRange,
        dates: this.weekDates,
        publishedAt: new Date(),
        publishedBy: user?.Name || 'Manager',
        status: 'published',
        orgId: this.orgId,
      };

      await setDoc(publishedRef, publishedData);
      this.lastPublished = new Date();
      this.publishedExists = true;

      await this.loadPublishedSchedules();

      const modalEl = document.getElementById('confirmPublishModal');
      const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
      modal?.hide();

      this.showToast(
        `Schedule published successfully for ${this.formatWeekLabel(
          this.publishWeekStart,
          this.publishWeekEnd,
        )}. If another schedule existed for this week, it has been replaced.`,
        'success',
      );
    } catch (error) {
      console.error('Error publishing schedule:', error);
      this.showToast(
        'Failed to publish schedule. Please try again shortly.',
        'error',
      );
    } finally {
      this.publishing = false;
    }
  }

  public formatWeekLabel(start: string, end: string): string {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    };

    return `${startDate.toLocaleDateString(
      'en-US',
      options,
    )} - ${endDate.toLocaleDateString('en-US', options)}`;
  }

  addNewRow() {
    const blankEmployee: ScheduleEntry = {
      name: '',
      Sun: '',
      Mon: '',
      Tue: '',
      Wed: '',
      Thu: '',
      Fri: '',
      Sat: '',
      totalHours: 0,
      daysOff: 0,
    };

    this.result.push(blankEmployee);

    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 100);
  }

  async openNotesOffcanvas() {
    const offcanvasElement = document.getElementById('notesOffcanvas');
    if (!offcanvasElement) return;

    const offcanvas = new (window as any).bootstrap.Offcanvas(offcanvasElement);
    offcanvas.show();

    await this.loadEmployeeNotes();
  }

  async loadEmployeeNotes() {
    if (!this.orgId) {
      this.showToast('No organization found.', 'error');
      return;
    }

    this.loadingNotes = true;
    this.employeeNotes = [];

    try {
      const employees = await this.firebase.getEmployeesByOrgId(this.orgId);

      // Filter to only include employees and managers (exclude admin and spectator roles)
      const filteredEmployees = employees.filter((emp: any) => {
        const role = emp.role?.toLowerCase() || '';
        return role === 'employee' || role === 'manager';
      });

      this.employeeNotes = filteredEmployees.map((emp: any) => ({
        id: emp.id,
        name: emp.name || emp.Name || 'Unnamed Employee',
        notes: emp.notes || emp.Notes || '',
      }));

      this.employeeNotes.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error loading employee notes:', error);
      this.showToast('Failed to load employee notes.', 'error');
    } finally {
      this.loadingNotes = false;
    }
  }

  openConfirmRemoveModal(index: number) {
    this.selectedRemoveIndex = index;
    const modalEl = document.getElementById('confirmRemoveModal');
    if (modalEl) {
      const modal = new (window as any).bootstrap.Modal(modalEl);
      modal.show();
    }
  }

  confirmRemove() {
    if (this.selectedRemoveIndex !== null) {
      this.result.splice(this.selectedRemoveIndex, 1);
      this.selectedRemoveIndex = null;
      this.showToast('Row removed successfully.', 'success');
    }

    const modalEl = document.getElementById('confirmRemoveModal');
    const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
    modal?.hide();
  }

  private parseTime12Hour(timeStr: string): number {
    const trimmed = timeStr.trim();
    const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

    if (!match) {
      throw new Error(`Invalid time format: "${timeStr}"`);
    }

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();

    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
      throw new Error(`Invalid time values in: "${timeStr}"`);
    }

    if (period === 'AM') {
      if (hours === 12) hours = 0;
    } else {
      if (hours !== 12) hours += 12;
    }

    return hours * 60 + minutes;
  }

  private calculateHoursFromRange(range: string): number {
    const parts = range.split('-').map((s) => s.trim());

    if (parts.length !== 2) {
      throw new Error(`Invalid range format: "${range}"`);
    }

    const startMinutes = this.parseTime12Hour(parts[0]);
    const endMinutes = this.parseTime12Hour(parts[1]);

    let totalMinutes = endMinutes - startMinutes;
    if (totalMinutes < 0) {
      totalMinutes += 24 * 60;
    }

    return totalMinutes / 60;
  }

  private calculateEmployeeHours(employee: ScheduleEntry): number {
    let totalHours = 0;

    for (const day of this.DAYS) {
      const shift = employee[day as keyof ScheduleEntry] as string;

      if (
        !shift ||
        shift.toLowerCase() === 'off' ||
        shift.toLowerCase() === 'requested off'
      ) {
        continue;
      }

      try {
        totalHours += this.calculateHoursFromRange(shift);
      } catch (error) {
        console.warn(
          `Error calculating hours for ${employee.name} on ${day}:`,
          error,
        );
      }
    }

    return parseFloat(totalHours.toFixed(2));
  }

  recalculateAllHours(showFeedback: boolean = true): void {
    if (!this.result || this.result.length === 0) {
      if (showFeedback) {
        this.showToast(
          'No schedule to calculate. Generate a schedule first.',
          'error',
        );
      }
      return;
    }

    let hoursUpdatedCount = 0;
    let daysOffUpdatedCount = 0;
    let errorCount = 0;

    for (const employee of this.result) {
      try {
        // Recalculate total hours
        const calculatedHours = this.calculateEmployeeHours(employee);

        if (employee.totalHours !== calculatedHours) {
          employee.totalHours = calculatedHours;
          hoursUpdatedCount++;
        }

        // Recalculate days off
        let daysOff = 0;
        for (const day of this.DAYS) {
          const shift = employee[day as keyof ScheduleEntry] as string;
          if (
            !shift ||
            shift.toLowerCase() === 'off' ||
            shift.toLowerCase() === 'requested off'
          ) {
            daysOff++;
          }
        }

        if (employee.daysOff !== daysOff) {
          employee.daysOff = daysOff;
          daysOffUpdatedCount++;
        }
      } catch (error) {
        console.error(`Error calculating hours for ${employee.name}:`, error);
        errorCount++;
      }
    }

    if (showFeedback) {
      if (errorCount > 0) {
        this.showToast(
          `Recalculated ${hoursUpdatedCount} hour(s) and ${daysOffUpdatedCount} days off. ${errorCount} errors occurred.`,
          'error',
        );
      } else {
        this.showToast(
          `Successfully recalculated ${hoursUpdatedCount} hour(s) and ${daysOffUpdatedCount} days off!`,
          'success',
        );
      }
    }
  }

  countOpenersAndClosers(): {
    [day: string]: {
      openers: number;
      closers: number;
      midShifts: number;
      totalWorkers: number;
    };
  } {
    const counts: {
      [day: string]: {
        openers: number;
        closers: number;
        midShifts: number;
        totalWorkers: number;
      };
    } = {};

    this.DAYS.forEach((day) => {
      counts[day] = { openers: 0, closers: 0, midShifts: 0, totalWorkers: 0 };
    });

    for (const employee of this.result) {
      for (const day of this.DAYS) {
        const shift = employee[day as keyof ScheduleEntry] as string;

        if (
          !shift ||
          shift.toLowerCase() === 'off' ||
          shift.toLowerCase() === 'requested off'
        ) {
          continue;
        }

        // Count total workers (anyone with a shift that's not off or requested off)
        counts[day].totalWorkers++;

        try {
          const parts = shift.split('-').map((s: string) => s.trim());
          if (parts.length !== 2) continue;

          const startMinutes = this.parseTime12Hour(parts[0]);
          const endMinutes = this.parseTime12Hour(parts[1]);

          // Opener: starts between 9:00 AM (540) and 11:00 AM (660), but NOT the mid shift
          if (
            startMinutes >= this.OPENING_TIME_START &&
            startMinutes <= this.OPENING_TIME_END &&
            !(startMinutes === 660 && endMinutes === 1140) // Exclude 11 AM - 7 PM mid shift
          ) {
            counts[day].openers++;
          }

          // Mid shift: 11:00 AM - 7:00 PM (660 - 1140)
          if (startMinutes === 660 && endMinutes === 1140) {
            counts[day].midShifts++;
          }

          // Closer: ends at 10:00 PM (1320)
          if (endMinutes === this.CLOSING_TIME) {
            counts[day].closers++;
          }
        } catch (error) {
          console.warn(
            `Error parsing shift for ${employee.name} on ${day}:`,
            error,
          );
        }
      }
    }

    return counts;
  }

  recountShifts(showFeedback: boolean = true): void {
    if (!this.result || this.result.length === 0) {
      if (showFeedback) {
        this.showToast(
          'No schedule to analyze. Generate a schedule first.',
          'error',
        );
      }
      return;
    }

    this.shiftsCount = this.countOpenersAndClosers();
    if (showFeedback) {
      this.showToast('Shift counts updated successfully!', 'success');
    }
  }

  recalculate(): void {
    if (!this.result || this.result.length === 0) {
      this.showToast(
        'No schedule to recalculate. Generate a schedule first.',
        'error',
      );
      return;
    }

    this.recalculateAllHours(false);
    this.recountShifts(false);
    this.showToast('Schedule hours and shift counts recalculated!', 'success');
  }

  private parseYMDLocal(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }

  setUpcomingWeek(): void {
    const today = new Date();
    const daysUntilSunday = (7 - today.getDay()) % 7; // 0 if today is Sunday
    const sunday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + daysUntilSunday,
    );
    const saturday = new Date(
      sunday.getFullYear(),
      sunday.getMonth(),
      sunday.getDate() + 6,
    );
    this.weekStart = this.toYMDLocal(sunday);
    this.weekEnd = this.toYMDLocal(saturday);
  }

  private toYMDLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private generateWeekDates(weekStart: string): string[] {
    const startDate = this.parseYMDLocal(weekStart);
    const dates: string[] = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      dates.push(this.toYMDLocal(d));
    }
    return dates;
  }

  showToast(message: string, type: 'success' | 'error') {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    setTimeout(() => (this.toastVisible = false), 3000);
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      try {
        this.employeeData = JSON.parse(reader.result as string);
      } catch {
        this.showToast('Invalid JSON file.', 'error');
      }
    };

    if (file) reader.readAsText(file);
  }

  getWeekLabel(): string {
    if (!this.weekStart || !this.weekEnd) return 'unspecified week';

    const start = new Date(this.weekStart);
    const end = new Date(this.weekEnd);

    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    };

    return `${start.toLocaleDateString(
      'en-US',
      options,
    )}–${end.toLocaleDateString('en-US', options)}`;
  }

  private validateInputs(): boolean {
    if (!this.employeeData) {
      this.showToast('Please upload employee JSON first.', 'error');
      return false;
    }

    if (!this.weekStart || !this.weekEnd) {
      this.showToast('Please select a week start and end date.', 'error');
      return false;
    }

    return true;
  }

  async downloadEmployeesJson(): Promise<void> {
    try {
      // 🏢 Get logged-in user's org ID
      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        console.error('No orgId found for current user.');
        return;
      }

      // 🔹 Query only employees + managers IN THIS ORG
      const employeesRef = collection(this.firestore, 'Employees');
      const q = query(
        employeesRef,
        where('role', 'in', ['employee', 'manager']),
        where('orgId', '==', orgId),
      );

      const employees$ = collectionData(q, { idField: 'id' });
      const employees = await firstValueFrom(employees$);

      // 🔹 Only include specific fields
      const filteredEmployees = employees.map((emp: any) => ({
        name: emp.Name || null, // ONLY Name
        availability: emp.availability
          ? {
              Sunday: emp.availability.Sunday || 'off',
              Monday: emp.availability.Monday || 'off',
              Tuesday: emp.availability.Tuesday || 'off',
              Wednesday: emp.availability.Wednesday || 'off',
              Thursday: emp.availability.Thursday || 'off',
              Friday: emp.availability.Friday || 'off',
              Saturday: emp.availability.Saturday || 'off',
            }
          : null,
        requestedOff: emp.requestedOff || [],
        fullTime: emp.fullTime ?? false,
        keyHolder: emp.keyHolder ?? false,
        cashier: emp.cashier ?? false,
        role: emp.role ?? null,
        orgId: emp.orgId ?? null,
      }));

      // 🔹 Create and download JSON
      const blob = new Blob([JSON.stringify(filteredEmployees, null, 2)], {
        type: 'application/json',
      });

      saveAs(blob, 'employees.json');
    } catch (err) {
      console.error('❌ Failed to fetch or download employees:', err);
    }
  }

  private parseAvailability(
    availStr: string,
  ): { start: number; end: number } | null {
    if (!availStr || availStr.toLowerCase() === 'off') {
      return null;
    }

    // Remove all spaces and convert to uppercase
    const cleaned = availStr.replace(/\s+/g, '').toUpperCase();

    // Try multiple patterns to parse the availability
    const patterns = [
      // Standard formats with AM/PM on both times
      /^(\d{1,2}):(\d{2})(AM|PM)-(\d{1,2}):(\d{2})(AM|PM)$/, // 11:00AM-6:30PM
      /^(\d{1,2})(AM|PM)-(\d{1,2}):(\d{2})(AM|PM)$/, // 11AM-6:30PM
      /^(\d{1,2}):(\d{2})(AM|PM)\/(\d{1,2}):(\d{2})(AM|PM)$/, // 4:00PM/9:30PM
      /^(\d{1,2})(AM|PM)\/(\d{1,2}):(\d{2})(AM|PM)$/, // 4PM/9:30PM

      // Formats with missing AM/PM on end time (assume PM if missing)
      /^(\d{1,2})(AM|PM)\/(\d{1,2}):(\d{2})$/, // 4pm/9:30 (no PM on end)
      /^(\d{1,2})(AM|PM)\/(\d{1,2})$/, // 4pm/9 (no time minutes on end)
    ];

    for (const pattern of patterns) {
      const match = cleaned.match(pattern);
      if (match) {
        try {
          let startHour = parseInt(match[1], 10);
          let startMin = 0;
          let startPeriod = '';
          let endHour = 0;
          let endMin = 0;
          let endPeriod = '';

          // Parse based on match length
          if (match.length === 7 || match.length === 8) {
            // Full format with both times having minutes and AM/PM
            if (match[2].match(/AM|PM/)) {
              // Format: 11AM-6:30PM or 4PM/9:30PM
              startPeriod = match[2];
              endHour = parseInt(match[3], 10);
              endMin = parseInt(match[4], 10);
              endPeriod = match[5];
            } else {
              // Format: 11:00AM-6:30PM
              startMin = parseInt(match[2], 10);
              startPeriod = match[3];
              endHour = parseInt(match[4], 10);
              endMin = parseInt(match[5], 10);
              endPeriod = match[6];
            }
          } else if (match.length === 5) {
            // Format: 4pm/9:30 (missing PM on end, assume PM)
            startPeriod = match[2];
            endHour = parseInt(match[3], 10);
            endMin = parseInt(match[4], 10);
            endPeriod = 'PM'; // Default to PM for end time
          } else if (match.length === 4) {
            // Format: 4pm/9 (missing both minutes and PM on end)
            startPeriod = match[2];
            endHour = parseInt(match[3], 10);
            endMin = 30; // Default to :30
            endPeriod = 'PM'; // Default to PM
          }

          // Convert to 24-hour format
          if (startPeriod === 'PM' && startHour !== 12) startHour += 12;
          if (startPeriod === 'AM' && startHour === 12) startHour = 0;
          if (endPeriod === 'PM' && endHour !== 12) endHour += 12;
          if (endPeriod === 'AM' && endHour === 12) endHour = 0;

          return {
            start: startHour * 60 + startMin,
            end: endHour * 60 + endMin,
          };
        } catch (e) {
          console.warn(`Error parsing availability "${availStr}":`, e);
          continue;
        }
      }
    }

    console.warn(`Could not parse availability format: "${availStr}"`);
    return null;
  }

  private isEmployeeRequestedOff(employee: Employee, date: string): boolean {
    if (!employee.requestedOff || employee.requestedOff.length === 0) {
      return false;
    }

    return employee.requestedOff.some((request) => {
      if (request.status !== 'approved') return false;

      const requestDate = new Date(request.date);
      const checkDate = new Date(date);

      return (
        requestDate.getFullYear() === checkDate.getFullYear() &&
        requestDate.getMonth() === checkDate.getMonth() &&
        requestDate.getDate() === checkDate.getDate()
      );
    });
  }

  private formatTime(minutes: number): string {
    let hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';

    if (hours > 12) hours -= 12;
    if (hours === 0) hours = 12;

    return `${hours}:${mins.toString().padStart(2, '0')} ${period}`;
  }

  private getShiftSlots(dayName: string): ShiftSlot[] {
    const isSunday = dayName === 'Sun';

    if (isSunday) {
      return [
        { start: 660, end: 1110, needsKeyHolder: true }, // 11:00 AM - 6:30 PM (Sunday opener/closer)
      ];
    }

    // Weekday shifts - FOCUSED ON 3 OPENERS, 3 CLOSERS, 1 MID (11AM-7PM)
    return [
      // OPENING SHIFTS (3 needed) - start between 9:00-11:00 AM
      { start: 540, end: 1020, needsKeyHolder: true }, // 9:00 AM - 5:00 PM (8h) PRIMARY OPENER
      { start: 570, end: 1050, needsKeyHolder: false }, // 9:30 AM - 5:30 PM (8h) OPENER 2
      { start: 600, end: 1080, needsKeyHolder: false }, // 10:00 AM - 6:00 PM (8h) OPENER 3

      // MID SHIFT (1 needed) - the specific 11:00 AM - 7:00 PM shift
      { start: 660, end: 1140, needsKeyHolder: false }, // 11:00 AM - 7:00 PM (8h) MID SHIFT

      // CLOSING SHIFTS (3 needed) - end at 10:00 PM
      { start: 780, end: 1320, needsKeyHolder: true }, // 1:00 PM - 10:00 PM (9h) PRIMARY CLOSER
      { start: 840, end: 1320, needsKeyHolder: false }, // 2:00 PM - 10:00 PM (8h) CLOSER 2
      { start: 900, end: 1320, needsKeyHolder: false }, // 3:00 PM - 10:00 PM (7h) CLOSER 3
    ];
  }

  private canWorkShift(
    employee: Employee,
    dayOfWeek: string,
    shift: ShiftSlot,
    currentHours: number,
  ): boolean {
    // Map short day names to full day names used in availability object
    const dayMap: { [key: string]: string } = {
      Sun: 'Sunday',
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
    };

    const fullDayName = dayMap[dayOfWeek] || dayOfWeek;
    const availability = employee.availability[fullDayName];

    if (!availability || availability.toLowerCase() === 'off') {
      return false;
    }

    const availWindow = this.parseAvailability(availability);
    if (!availWindow) {
      return false;
    }

    if (shift.start < availWindow.start || shift.end > availWindow.end) {
      return false;
    }

    const shiftHours = (shift.end - shift.start) / 60;
    const projectedHours = currentHours + shiftHours;

    if (employee.fullTime) {
      return projectedHours <= 42;
    } else {
      return projectedHours <= this.PART_TIME_MAX_HOURS;
    }
  }

  generate() {
    if (!this.validateInputs()) return;

    if (!this.scheduleGenerationInProgress) {
      this.scheduleGenerationAttempts = 0;
      this.scheduleGenerationInProgress = true;
    }

    this.loading = true;
    this.result = [];

    try {
      this.weekDates = this.generateWeekDates(this.weekStart);

      const employeeHours: Map<string, number> = new Map();
      const employeeSchedules: Map<string, Map<string, string>> = new Map();
      const employeeDaysWorked: Map<string, number> = new Map();

      this.employeeData!.forEach((emp) => {
        employeeHours.set(emp.name, 0);
        employeeSchedules.set(emp.name, new Map());
        employeeDaysWorked.set(emp.name, 0);
      });

      console.log('\n========== SCHEDULE GENERATION START ==========');

      let totalIterations = 0;

      // STEP 1: Mark all requested days off
      this.employeeData!.forEach((emp) => {
        if (!emp.requestedOff || emp.requestedOff.length === 0) return;

        emp.requestedOff.forEach((request) => {
          if (request.status !== 'approved') return;

          for (let i = 0; i < 7; i++) {
            const dateStr = this.weekDates[i];
            if (this.isEmployeeRequestedOff(emp, dateStr)) {
              const dayName = this.DAYS[i];
              employeeSchedules
                .get(emp.name)!
                .set(dayName, 'REQUESTED_OFF_PLACEHOLDER');
            }
          }
        });
      });

      // STEP 2: Assign shifts in priority order (3 openers, 1 mid, 3 closers)
      const keyHolders = this.employeeData!.filter((emp) => emp.keyHolder);

      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dayName = this.DAYS[dayIndex];
        const isSunday = dayName === 'Sun';

        if (isSunday) continue; // Skip Sunday for opener/closer logic

        const allShifts = this.getShiftSlots(dayName);

        // Separate shifts by type
        const openerShifts = allShifts.filter(
          (s) =>
            s.start >= this.OPENING_TIME_START &&
            s.start <= this.OPENING_TIME_END &&
            s.start !== 660, // Exclude the 11 AM mid shift
        );
        const midShifts = allShifts.filter(
          (s) => s.start === 660 && s.end === 1140,
        ); // 11 AM - 7 PM
        const closerShifts = allShifts.filter(
          (s) => s.end === this.CLOSING_TIME && s.start >= 780,
        );

        // ASSIGN 3 OPENERS
        for (
          let i = 0;
          i < this.TARGET_OPENERS && i < openerShifts.length;
          i++
        ) {
          const shift = openerShifts[i];
          const requireKeyHolder = shift.needsKeyHolder;
          const candidatePool = requireKeyHolder
            ? keyHolders
            : this.employeeData!;

          const candidates = candidatePool
            .filter((emp) => {
              if (employeeSchedules.get(emp.name)!.has(dayName)) return false;
              const hours = employeeHours.get(emp.name) || 0;
              return this.canWorkShift(emp, dayName, shift, hours);
            })
            .sort((a, b) => {
              const hoursA = employeeHours.get(a.name) || 0;
              const hoursB = employeeHours.get(b.name) || 0;
              const daysA = employeeDaysWorked.get(a.name) || 0;
              const daysB = employeeDaysWorked.get(b.name) || 0;

              if (a.fullTime && !b.fullTime) return -1;
              if (!a.fullTime && b.fullTime) return 1;
              if (daysA !== daysB) return daysA - daysB;
              return hoursA - hoursB;
            });

          if (candidates.length > 0) {
            this.assignShift(
              candidates[0],
              dayName,
              shift,
              employeeSchedules,
              employeeHours,
              employeeDaysWorked,
              new Set(),
            );
            totalIterations++;
          }
        }

        // ASSIGN 1 MID SHIFT (11 AM - 7 PM)
        for (
          let i = 0;
          i < this.TARGET_MID_SHIFTS && i < midShifts.length;
          i++
        ) {
          const shift = midShifts[i];

          const candidates = this.employeeData!.filter((emp) => {
            if (employeeSchedules.get(emp.name)!.has(dayName)) return false;
            const hours = employeeHours.get(emp.name) || 0;
            return this.canWorkShift(emp, dayName, shift, hours);
          }).sort((a, b) => {
            const hoursA = employeeHours.get(a.name) || 0;
            const hoursB = employeeHours.get(b.name) || 0;
            const daysA = employeeDaysWorked.get(a.name) || 0;
            const daysB = employeeDaysWorked.get(b.name) || 0;

            if (a.fullTime && !b.fullTime) return -1;
            if (!a.fullTime && b.fullTime) return 1;
            if (daysA !== daysB) return daysA - daysB;
            return hoursA - hoursB;
          });

          if (candidates.length > 0) {
            this.assignShift(
              candidates[0],
              dayName,
              shift,
              employeeSchedules,
              employeeHours,
              employeeDaysWorked,
              new Set(),
            );
            totalIterations++;
          }
        }

        // ASSIGN 3 CLOSERS
        for (
          let i = 0;
          i < this.TARGET_CLOSERS && i < closerShifts.length;
          i++
        ) {
          const shift = closerShifts[i];
          const requireKeyHolder = shift.needsKeyHolder;
          const candidatePool = requireKeyHolder
            ? keyHolders
            : this.employeeData!;

          const candidates = candidatePool
            .filter((emp) => {
              if (employeeSchedules.get(emp.name)!.has(dayName)) return false;
              const hours = employeeHours.get(emp.name) || 0;
              return this.canWorkShift(emp, dayName, shift, hours);
            })
            .sort((a, b) => {
              const hoursA = employeeHours.get(a.name) || 0;
              const hoursB = employeeHours.get(b.name) || 0;
              const daysA = employeeDaysWorked.get(a.name) || 0;
              const daysB = employeeDaysWorked.get(b.name) || 0;

              if (a.fullTime && !b.fullTime) return -1;
              if (!a.fullTime && b.fullTime) return 1;
              if (daysA !== daysB) return daysA - daysB;
              return hoursA - hoursB;
            });

          if (candidates.length > 0) {
            this.assignShift(
              candidates[0],
              dayName,
              shift,
              employeeSchedules,
              employeeHours,
              employeeDaysWorked,
              new Set(),
            );
            totalIterations++;
          }
        }
      }

      // STEP 3: FILL TO EXACTLY 7 WORKERS PER DAY
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dayName = this.DAYS[dayIndex];
        const isSunday = dayName === 'Sun';
        const targetWorkers = 7; // Exactly 7 workers per day

        let currentCount = this.countAssignedOnDay(dayName, employeeSchedules);

        // Fill to exactly 7 workers
        while (currentCount < targetWorkers) {
          let addedWorker = false;
          const allShifts = this.getShiftSlots(dayName);

          for (const shift of allShifts) {
            if (currentCount >= targetWorkers) break;

            const available = this.employeeData!.filter((emp) => {
              if (employeeSchedules.get(emp.name)!.has(dayName)) return false;
              const hours = employeeHours.get(emp.name) || 0;
              const maxHours = emp.fullTime ? 42 : 28;

              if (hours >= maxHours - 3) return false;
              return this.canWorkShift(emp, dayName, shift, hours);
            }).sort((a, b) => {
              const hoursA = employeeHours.get(a.name) || 0;
              const hoursB = employeeHours.get(b.name) || 0;
              const daysA = employeeDaysWorked.get(a.name) || 0;
              const daysB = employeeDaysWorked.get(b.name) || 0;

              if (daysA !== daysB) return daysA - daysB;
              return hoursA - hoursB;
            });

            if (available.length > 0) {
              const emp = available[0];
              let assignedShift: ShiftSlot | null = null;

              if (
                this.canWorkShift(
                  emp,
                  dayName,
                  shift,
                  employeeHours.get(emp.name) || 0,
                )
              ) {
                assignedShift = shift;
              } else {
                const dayMap: { [key: string]: string } = {
                  Sun: 'Sunday',
                  Mon: 'Monday',
                  Tue: 'Tuesday',
                  Wed: 'Wednesday',
                  Thu: 'Thursday',
                  Fri: 'Friday',
                  Sat: 'Saturday',
                };
                const fullDayName = dayMap[dayName];
                assignedShift = this.createCustomShift(
                  emp,
                  fullDayName,
                  employeeHours.get(emp.name) || 0,
                  emp.fullTime ? 42 : 28,
                  isSunday,
                );
              }

              if (assignedShift) {
                this.assignShift(
                  emp,
                  dayName,
                  assignedShift,
                  employeeSchedules,
                  employeeHours,
                  employeeDaysWorked,
                  new Set(),
                );
                currentCount++;
                addedWorker = true;
                totalIterations++;
                break;
              }
            }
          }

          if (!addedWorker) {
            // Try to assign any available employee to a custom shift before giving up
            let customAssigned = false;
            for (const emp of this.employeeData!) {
              if (employeeSchedules.get(emp.name)!.has(dayName)) continue;
              const hours = employeeHours.get(emp.name) || 0;
              const maxHours = emp.fullTime ? 42 : 28;
              if (hours >= maxHours - 3) continue;
              const dayMap: { [key: string]: string } = {
                Sun: 'Sunday',
                Mon: 'Monday',
                Tue: 'Tuesday',
                Wed: 'Wednesday',
                Thu: 'Thursday',
                Fri: 'Friday',
                Sat: 'Saturday',
              };
              const fullDayName = dayMap[dayName];
              const customShift = this.createCustomShift(
                emp,
                fullDayName,
                hours,
                maxHours,
                isSunday,
              );
              if (customShift) {
                this.assignShift(
                  emp,
                  dayName,
                  customShift,
                  employeeSchedules,
                  employeeHours,
                  employeeDaysWorked,
                  new Set(),
                );
                currentCount++;
                customAssigned = true;
                totalIterations++;
                break;
              }
            }
            if (!customAssigned) {
              console.warn(
                `Could not fill ${dayName} to 7 workers. Current: ${currentCount}`,
              );
              break;
            }
          }
        }
      }

      // STEP 3.5: ENFORCE MINIMUM 3 OPENERS AND 3 CLOSERS PER DAY
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dayName = this.DAYS[dayIndex];
        const isSunday = dayName === 'Sun';

        if (!isSunday) {
          const openerShifts = this.getShiftSlots(dayName).filter(
            (s) =>
              s.start >= this.OPENING_TIME_START &&
              s.start <= this.OPENING_TIME_END &&
              s.start !== 660, // Exclude mid shift
          );
          const closerShifts = this.getShiftSlots(dayName).filter(
            (s) => s.end === this.CLOSING_TIME && s.start >= 780,
          );

          // ENFORCE 3+ OPENERS
          let openerCount = this.countOpenersOnDay(dayName, employeeSchedules);
          while (openerCount < 3) {
            let assigned = false;
            for (const shift of openerShifts) {
              if (assigned) break;
              const available = this.employeeData!.filter((emp) => {
                if (employeeSchedules.get(emp.name)!.has(dayName)) return false;
                const hours = employeeHours.get(emp.name) || 0;
                return this.canWorkShift(emp, dayName, shift, hours);
              }).sort((a, b) => {
                const hoursA = employeeHours.get(a.name) || 0;
                const hoursB = employeeHours.get(b.name) || 0;
                const daysA = employeeDaysWorked.get(a.name) || 0;
                const daysB = employeeDaysWorked.get(b.name) || 0;
                if (daysA !== daysB) return daysA - daysB;
                return hoursA - hoursB;
              });

              if (available.length > 0) {
                this.assignShift(
                  available[0],
                  dayName,
                  shift,
                  employeeSchedules,
                  employeeHours,
                  employeeDaysWorked,
                  new Set(),
                );
                openerCount++;
                assigned = true;
                totalIterations++;
              }
            }

            // If no standard shift worked, try creating custom shifts for available employees
            if (!assigned) {
              const dayMap: { [key: string]: string } = {
                Sun: 'Sunday',
                Mon: 'Monday',
                Tue: 'Tuesday',
                Wed: 'Wednesday',
                Thu: 'Thursday',
                Fri: 'Friday',
                Sat: 'Saturday',
              };
              const fullDayName = dayMap[dayName];

              const available = this.employeeData!.filter((emp) => {
                if (employeeSchedules.get(emp.name)!.has(dayName)) return false;
                const hours = employeeHours.get(emp.name) || 0;
                const maxHours = emp.fullTime ? 42 : 28;
                if (hours >= maxHours - 3) return false;
                const avail = emp.availability[fullDayName];
                return avail && avail.toLowerCase() !== 'off';
              }).sort((a, b) => {
                const hoursA = employeeHours.get(a.name) || 0;
                const hoursB = employeeHours.get(b.name) || 0;
                const daysA = employeeDaysWorked.get(a.name) || 0;
                const daysB = employeeDaysWorked.get(b.name) || 0;
                if (daysA !== daysB) return daysA - daysB;
                return hoursA - hoursB;
              });

              if (available.length > 0) {
                const emp = available[0];
                const customShift = this.createCustomShift(
                  emp,
                  fullDayName,
                  employeeHours.get(emp.name) || 0,
                  emp.fullTime ? 42 : 28,
                  false,
                );
                if (customShift) {
                  this.assignShift(
                    emp,
                    dayName,
                    customShift,
                    employeeSchedules,
                    employeeHours,
                    employeeDaysWorked,
                    new Set(),
                  );
                  openerCount++;
                  assigned = true;
                  totalIterations++;
                }
              }
            }

            if (!assigned) {
              console.warn(
                `Could not assign enough openers to ${dayName}. Current: ${openerCount}`,
              );
              break;
            }
          }

          // ENFORCE 3+ CLOSERS
          let closerCount = this.countClosersOnDay(dayName, employeeSchedules);
          while (closerCount < 3) {
            let assigned = false;
            for (const shift of closerShifts) {
              if (assigned) break;
              const available = this.employeeData!.filter((emp) => {
                if (employeeSchedules.get(emp.name)!.has(dayName)) return false;
                const hours = employeeHours.get(emp.name) || 0;
                return this.canWorkShift(emp, dayName, shift, hours);
              }).sort((a, b) => {
                const hoursA = employeeHours.get(a.name) || 0;
                const hoursB = employeeHours.get(b.name) || 0;
                const daysA = employeeDaysWorked.get(a.name) || 0;
                const daysB = employeeDaysWorked.get(b.name) || 0;
                if (daysA !== daysB) return daysA - daysB;
                return hoursA - hoursB;
              });

              if (available.length > 0) {
                this.assignShift(
                  available[0],
                  dayName,
                  shift,
                  employeeSchedules,
                  employeeHours,
                  employeeDaysWorked,
                  new Set(),
                );
                closerCount++;
                assigned = true;
                totalIterations++;
              }
            }

            // If no standard shift worked, try creating custom shifts for available employees
            if (!assigned) {
              const dayMap: { [key: string]: string } = {
                Sun: 'Sunday',
                Mon: 'Monday',
                Tue: 'Tuesday',
                Wed: 'Wednesday',
                Thu: 'Thursday',
                Fri: 'Friday',
                Sat: 'Saturday',
              };
              const fullDayName = dayMap[dayName];

              const available = this.employeeData!.filter((emp) => {
                if (employeeSchedules.get(emp.name)!.has(dayName)) return false;
                const hours = employeeHours.get(emp.name) || 0;
                const maxHours = emp.fullTime ? 42 : 28;
                if (hours >= maxHours - 3) return false;
                const avail = emp.availability[fullDayName];
                return avail && avail.toLowerCase() !== 'off';
              }).sort((a, b) => {
                const hoursA = employeeHours.get(a.name) || 0;
                const hoursB = employeeHours.get(b.name) || 0;
                const daysA = employeeDaysWorked.get(a.name) || 0;
                const daysB = employeeDaysWorked.get(b.name) || 0;
                if (daysA !== daysB) return daysA - daysB;
                return hoursA - hoursB;
              });

              if (available.length > 0) {
                const emp = available[0];
                const customShift = this.createCustomShift(
                  emp,
                  fullDayName,
                  employeeHours.get(emp.name) || 0,
                  emp.fullTime ? 42 : 28,
                  false,
                );
                if (customShift) {
                  this.assignShift(
                    emp,
                    dayName,
                    customShift,
                    employeeSchedules,
                    employeeHours,
                    employeeDaysWorked,
                    new Set(),
                  );
                  closerCount++;
                  assigned = true;
                  totalIterations++;
                }
              }
            }

            if (!assigned) {
              console.warn(
                `Could not assign enough closers to ${dayName}. Current: ${closerCount}`,
              );
              break;
            }
          }
        }
      }

      // STEP 4: ENFORCE KEY HOLDER COVERAGE REQUIREMENTS
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dayName = this.DAYS[dayIndex];
        const isSunday = dayName === 'Sun';

        if (isSunday) {
          // Sunday: At least 1 key holder anytime during the day
          const hasKeyHolder = this.hasKeyHolderOnDay(
            dayName,
            employeeSchedules,
          );
          if (!hasKeyHolder) {
            this.assignKeyHolderToDay(
              dayName,
              employeeSchedules,
              employeeHours,
              employeeDaysWorked,
            );
            totalIterations++;
          }
        } else {
          // Weekday: At least 1 key holder in opening shift AND 1 in closing shift
          const hasOpenerKeyHolder = this.hasKeyHolderInShift(
            dayName,
            'opener',
            employeeSchedules,
          );
          const hasCloserKeyHolder = this.hasKeyHolderInShift(
            dayName,
            'closer',
            employeeSchedules,
          );

          if (!hasOpenerKeyHolder) {
            this.assignKeyHolderToShift(
              dayName,
              'opener',
              employeeSchedules,
              employeeHours,
              employeeDaysWorked,
            );
            totalIterations++;
          }

          if (!hasCloserKeyHolder) {
            this.assignKeyHolderToShift(
              dayName,
              'closer',
              employeeSchedules,
              employeeHours,
              employeeDaysWorked,
            );
            totalIterations++;
          }
        }
      }

      // STEP 4.5: IF >= 2 KEY HOLDERS, ENFORCE ONE 9:00 AM OPENER AND ONE 2:00 PM CLOSER
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dayName = this.DAYS[dayIndex];
        const isSunday = dayName === 'Sun';

        if (isSunday) continue; // Skip Sunday

        const keyHoldersOnDay = this.employeeData!.filter((emp) => {
          if (!emp.keyHolder) return false;
          const shift = employeeSchedules.get(emp.name)?.get(dayName);
          return shift && shift !== 'REQUESTED_OFF_PLACEHOLDER';
        });

        if (keyHoldersOnDay.length >= 2) {
          // Find key holder assigned to 9:00 AM opener (start: 540)
          let keyHolderIn9AM = keyHoldersOnDay.find((emp) => {
            const shift = employeeSchedules.get(emp.name)?.get(dayName);
            if (!shift) return false;
            try {
              const parts = shift.split(' - ').map((s) => s.trim());
              if (parts.length !== 2) return false;
              const startMinutes = this.parseTime12Hour(parts[0]);
              return startMinutes === 540;
            } catch {
              return false;
            }
          });

          // Find key holder assigned to 2:00 PM closer (start: 840)
          let keyHolderIn2PM = keyHoldersOnDay.find((emp) => {
            const shift = employeeSchedules.get(emp.name)?.get(dayName);
            if (!shift) return false;
            try {
              const parts = shift.split(' - ').map((s) => s.trim());
              if (parts.length !== 2) return false;
              const startMinutes = this.parseTime12Hour(parts[0]);
              return startMinutes === 840;
            } catch {
              return false;
            }
          });

          // Enforce 9:00 AM opener
          if (!keyHolderIn9AM) {
            const target9AMShift = {
              start: 540,
              end: 1020,
              needsKeyHolder: true,
            };

            // Find a key holder to reassign (prefer one not in 2PM slot)
            const candidate =
              keyHoldersOnDay.find(
                (emp) => emp.name !== keyHolderIn2PM?.name,
              ) || keyHoldersOnDay[0];

            // Remove old shift hours
            const oldShift = employeeSchedules
              .get(candidate.name)
              ?.get(dayName);
            if (oldShift && oldShift !== 'REQUESTED_OFF_PLACEHOLDER') {
              try {
                const parts = oldShift.split(' - ').map((s) => s.trim());
                const shiftHours =
                  (this.parseTime12Hour(parts[1]) -
                    this.parseTime12Hour(parts[0])) /
                  60;
                const currentHours = employeeHours.get(candidate.name) || 0;
                employeeHours.set(candidate.name, currentHours - shiftHours);
              } catch {
                // Ignore parsing errors
              }
            }

            // Assign to 9:00 AM opener
            this.assignShift(
              candidate,
              dayName,
              target9AMShift,
              employeeSchedules,
              employeeHours,
              employeeDaysWorked,
              new Set(),
            );
            keyHolderIn9AM = candidate;
            totalIterations++;
          }

          // Enforce 2:00 PM closer
          if (!keyHolderIn2PM) {
            const target2PMShift = {
              start: 840,
              end: 1320,
              needsKeyHolder: false,
            };

            // Find a DIFFERENT key holder to reassign (prefer one not in 9AM slot)
            const candidate =
              keyHoldersOnDay.find(
                (emp) =>
                  emp.name !== keyHolderIn9AM?.name &&
                  emp.name !== keyHolderIn2PM?.name,
              ) ||
              keyHoldersOnDay.find((emp) => emp.name !== keyHolderIn9AM?.name);

            if (candidate) {
              // Remove old shift hours
              const oldShift = employeeSchedules
                .get(candidate.name)
                ?.get(dayName);
              if (oldShift && oldShift !== 'REQUESTED_OFF_PLACEHOLDER') {
                try {
                  const parts = oldShift.split(' - ').map((s) => s.trim());
                  const shiftHours =
                    (this.parseTime12Hour(parts[1]) -
                      this.parseTime12Hour(parts[0])) /
                    60;
                  const currentHours = employeeHours.get(candidate.name) || 0;
                  employeeHours.set(candidate.name, currentHours - shiftHours);
                } catch {
                  // Ignore parsing errors
                }
              }

              // Assign to 2:00 PM closer
              this.assignShift(
                candidate,
                dayName,
                target2PMShift,
                employeeSchedules,
                employeeHours,
                employeeDaysWorked,
                new Set(),
              );
              keyHolderIn2PM = candidate;
              totalIterations++;
            }
          }
        }
      }

      // STEP 5: ENFORCE CASHIER COVERAGE REQUIREMENT (AT LEAST 1 CASHIER PER DAY)
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dayName = this.DAYS[dayIndex];
        const hasCashier = this.hasCashierOnDay(dayName, employeeSchedules);

        if (!hasCashier) {
          this.assignCashierToDay(
            dayName,
            employeeSchedules,
            employeeHours,
            employeeDaysWorked,
          );
          totalIterations++;
        }
      }

      // STEP 6: Ensure no one has 7 days off
      const unscheduled = this.employeeData!.filter((emp) => {
        const days = employeeDaysWorked.get(emp.name) || 0;
        const requestedDays = this.DAYS.filter(
          (day) =>
            employeeSchedules.get(emp.name)!.get(day) ===
            'REQUESTED_OFF_PLACEHOLDER',
        ).length;
        return days === 0 && requestedDays < 7;
      });

      if (unscheduled.length > 0) {
        for (const emp of unscheduled) {
          let assigned = false;

          for (let dayIndex = 0; dayIndex < 7 && !assigned; dayIndex++) {
            const dayName = this.DAYS[dayIndex];
            if (employeeSchedules.get(emp.name)!.has(dayName)) continue;

            // Check if day is already at max capacity
            const currentCount = this.countAssignedOnDay(
              dayName,
              employeeSchedules,
            );
            if (currentCount >= this.MAX_WORKERS_PER_DAY) continue;

            const dayMap: { [key: string]: string } = {
              Sun: 'Sunday',
              Mon: 'Monday',
              Tue: 'Tuesday',
              Wed: 'Wednesday',
              Thu: 'Thursday',
              Fri: 'Friday',
              Sat: 'Saturday',
            };
            const fullDayName = dayMap[dayName];
            const avail = emp.availability[fullDayName];

            if (!avail || avail.toLowerCase() === 'off') continue;

            const shifts = this.getShiftSlots(dayName);
            const hours = employeeHours.get(emp.name) || 0;

            for (const shift of shifts) {
              if (this.canWorkShift(emp, dayName, shift, hours)) {
                this.assignShift(
                  emp,
                  dayName,
                  shift,
                  employeeSchedules,
                  employeeHours,
                  employeeDaysWorked,
                  new Set(),
                );
                totalIterations++;
                assigned = true;
                break;
              }
            }

            if (!assigned) {
              const customShift = this.createCustomShift(
                emp,
                fullDayName,
                hours,
                emp.fullTime ? 42 : 28,
                dayName === 'Sun',
              );
              if (customShift) {
                this.assignShift(
                  emp,
                  dayName,
                  customShift,
                  employeeSchedules,
                  employeeHours,
                  employeeDaysWorked,
                  new Set(),
                );
                totalIterations++;
                assigned = true;
              }
            }
          }
        }
      }

      // Build final schedule
      const schedule: ScheduleEntry[] = [];
      this.employeeData!.forEach((emp) => {
        const empSchedule = employeeSchedules.get(emp.name)!;
        const entry: ScheduleEntry = {
          name: emp.name,
          Sun: '',
          Mon: '',
          Tue: '',
          Wed: '',
          Thu: '',
          Fri: '',
          Sat: '',
          totalHours: 0,
          daysOff: 0,
        };

        let daysOff = 0;
        this.DAYS.forEach((day, index) => {
          const dateStr = this.weekDates[index];
          const scheduled = empSchedule.get(day);

          if (scheduled === 'REQUESTED_OFF_PLACEHOLDER') {
            (entry as any)[day] = 'requested off';
            daysOff++;
          } else if (scheduled) {
            (entry as any)[day] = scheduled;
          } else {
            (entry as any)[day] = 'off';
            daysOff++;
          }
        });

        entry.totalHours = parseFloat(
          (employeeHours.get(emp.name) || 0).toFixed(2),
        );
        entry.daysOff = daysOff;
        schedule.push(entry);
      });

      this.result = schedule.sort((a, b) => a.name.localeCompare(b.name));

      const validation = this.validateGeneratedSchedule(this.result);

      if (!validation.allRulesPassed) {
        this.scheduleGenerationAttempts++;
        console.log(
          `Schedule generation attempt ${this.scheduleGenerationAttempts} failed. Missing rules: ${validation.failedRules.join(', ')}`,
        );

        if (this.scheduleGenerationAttempts < 20) {
          return this.generate();
        }

        console.log(
          'Reached 20 schedule generation attempts without finding a schedule that passes all rules.',
        );
      }

      // Count openers and closers for the generated schedule
      this.shiftsCount = this.countOpenersAndClosers();

      // GENERATE COMPREHENSIVE FINAL REPORT
      this.generateFinalReport(
        totalIterations,
        employeeSchedules,
        employeeHours,
        employeeDaysWorked,
      );

      if (validation.allRulesPassed) {
        this.showToast('Schedule generated successfully!', 'success');
      } else {
        this.showToast(
          'Generated the best available schedule after 20 attempts',
          'success',
        );
      }

      this.scheduleGenerationInProgress = false;
    } catch (error) {
      console.error('Error generating schedule:', error);
      this.showToast('Error generating schedule. Check console.', 'error');
    } finally {
      this.scheduleGenerationInProgress = false;
      this.loading = false;
    }
  }

  private generateFinalReport(
    totalIterations: number,
    employeeSchedules: Map<string, Map<string, string>>,
    employeeHours: Map<string, number>,
    employeeDaysWorked: Map<string, number>,
  ): void {
    console.log(
      '\n╔════════════════════════════════════════════════════════════════╗',
    );
    console.log(
      '║              SCHEDULE GENERATION COMPLETE                      ║',
    );
    console.log(
      '╚════════════════════════════════════════════════════════════════╝\n',
    );

    console.log(`📊 Total Assignments Made: ${totalIterations}`);
    console.log(`👥 Total Employees: ${this.employeeData!.length}\n`);

    // Daily Coverage Analysis
    console.log('📅 DAILY COVERAGE ANALYSIS:');
    console.log(
      '─────────────────────────────────────────────────────────────────',
    );

    const dayIssues: string[] = [];

    for (let i = 0; i < 7; i++) {
      const dayName = this.DAYS[i];
      const isSunday = dayName === 'Sun';
      const minWorkers = isSunday
        ? this.MIN_SUNDAY_WORKERS
        : this.MIN_WEEKDAY_WORKERS;
      const count = this.countAssignedOnDay(dayName, employeeSchedules);
      const counts = this.countOpenersClosersOnDay(dayName, employeeSchedules);

      const status =
        count >= minWorkers && count <= this.MAX_WORKERS_PER_DAY ? '✓' : '⚠️';
      const dayFullName = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ][i];

      console.log(
        `${status} ${dayFullName.padEnd(10)} ${count
          .toString()
          .padStart(
            2,
          )} workers (min: ${minWorkers}, max: ${this.MAX_WORKERS_PER_DAY})  |  ${
          counts.openers
        } openers, ${counts.closers} closers, ${counts.totalWorkers} total`,
      );

      if (count < minWorkers) {
        const shortage = minWorkers - count;
        dayIssues.push(
          `${dayFullName}: SHORT ${shortage} worker(s) - Need ${minWorkers}, have ${count}`,
        );
      }

      if (count > this.MAX_WORKERS_PER_DAY) {
        const excess = count - this.MAX_WORKERS_PER_DAY;
        dayIssues.push(
          `${dayFullName}: OVERSTAFFED by ${excess} worker(s) - Max ${this.MAX_WORKERS_PER_DAY}, have ${count}`,
        );
      }

      if (!isSunday && counts.openers !== this.TARGET_OPENERS) {
        dayIssues.push(
          `${dayFullName}: ${counts.openers} openers (target: ${this.TARGET_OPENERS})`,
        );
      }

      if (!isSunday && counts.closers !== this.TARGET_CLOSERS) {
        dayIssues.push(
          `${dayFullName}: ${counts.closers} closers (target: ${this.TARGET_CLOSERS})`,
        );
      }

      if (!this.hasCashierOnDay(dayName, employeeSchedules)) {
        dayIssues.push(`${dayFullName}: no cashier scheduled`);
      }
    }

    // Employee Analysis
    console.log('\n👤 EMPLOYEE ANALYSIS:');
    console.log(
      '─────────────────────────────────────────────────────────────────',
    );

    const fullTimeIssues: string[] = [];
    const partTimeIssues: string[] = [];
    const zeroShiftEmployees: string[] = [];
    const requestedOffEmployees: string[] = [];

    this.result.forEach((emp) => {
      const employee = this.employeeData!.find((e) => e.name === emp.name);
      if (!employee) return;

      const worked = 7 - emp.daysOff;
      const requestedDays = this.DAYS.filter(
        (day) => emp[day as keyof ScheduleEntry] === 'requested off',
      ).length;

      let status = '✓';
      let issue = '';

      if (worked === 0) {
        if (requestedDays === 7) {
          status = '📅';
          issue = 'All 7 days requested off';
          requestedOffEmployees.push(emp.name);
        } else {
          status = '❌';
          issue = 'NO SHIFTS ASSIGNED';
          zeroShiftEmployees.push(emp.name);
        }
      } else if (employee.fullTime) {
        if (emp.totalHours < 32) {
          status = '⚠️';
          issue = `Under 32h (has ${emp.totalHours}h)`;
          fullTimeIssues.push(
            `${emp.name}: Only ${emp.totalHours}h (target: 42h, min: 32h) - ${worked} days worked`,
          );
        } else if (emp.totalHours < 42) {
          status = '⚠️';
          issue = `Below 42h target (has ${emp.totalHours}h)`;
          fullTimeIssues.push(
            `${emp.name}: ${emp.totalHours}h (target: 42h) - ${worked} days worked`,
          );
        }
      }

      const ftLabel = employee.fullTime ? 'FT' : 'PT';
      const khLabel = employee.keyHolder ? '🔑' : '  ';

      console.log(
        `${status} ${khLabel} ${emp.name.padEnd(
          20,
        )} ${ftLabel}  ${emp.totalHours
          .toString()
          .padStart(5)}h  ${worked} days  ${issue}`,
      );
    });

    // Issues Summary
    if (
      dayIssues.length > 0 ||
      fullTimeIssues.length > 0 ||
      partTimeIssues.length > 0 ||
      zeroShiftEmployees.length > 0
    ) {
      console.log('\n⚠️  ISSUES DETECTED:');
      console.log(
        '═════════════════════════════════════════════════════════════════',
      );

      if (dayIssues.length > 0) {
        console.log('\n📅 Day Coverage Issues:');
        dayIssues.forEach((issue) => console.log(`   • ${issue}`));
        console.log(
          '\n   💡 Why: Not enough available employees or employees already at hour limits',
        );
        console.log(
          '   🔧 Fix: Add more employees, increase availability, or reduce other days',
        );
      }

      if (zeroShiftEmployees.length > 0) {
        console.log('\n❌ Employees with NO Shifts:');
        zeroShiftEmployees.forEach((name) => {
          const employee = this.employeeData!.find((e) => e.name === name);
          if (!employee) return;

          const availableDays = this.DAYS.filter((day) => {
            const dayMap: { [key: string]: string } = {
              Sun: 'Sunday',
              Mon: 'Monday',
              Tue: 'Tuesday',
              Wed: 'Wednesday',
              Thu: 'Thursday',
              Fri: 'Friday',
              Sat: 'Saturday',
            };
            const fullDayName = dayMap[day];
            const avail = employee.availability[fullDayName];
            return avail && avail.toLowerCase() !== 'off';
          });

          console.log(`   • ${name}`);
          console.log(`     Available: ${availableDays.join(', ') || 'NONE'}`);
          console.log(
            `     💡 Why: ${
              availableDays.length === 0
                ? 'No availability set'
                : 'Availability conflicts with business needs or all days already at max capacity (7 workers)'
            }`,
          );
        });
      }

      if (fullTimeIssues.length > 0) {
        console.log('\n⚠️  Full-Time Employees Under Target:');
        fullTimeIssues.forEach((issue) => console.log(`   • ${issue}`));
        console.log(
          '\n   💡 Why: Limited availability or days already at max capacity',
        );
        console.log(
          '   🔧 Fix: Increase employee availability or adjust other schedules',
        );
      }

      if (requestedOffEmployees.length > 0) {
        console.log('\n📅 Employees with All Days Requested Off:');
        requestedOffEmployees.forEach((name) => console.log(`   • ${name}`));
        console.log(
          '   ℹ️  This is expected - these employees have approved time off',
        );
      }
    } else {
      console.log('\n✅ NO ISSUES DETECTED - All targets met!');
    }

    console.log(
      '\n═════════════════════════════════════════════════════════════════\n',
    );
  }

  // ADD THESE HELPER METHODS:

  private countAssignedOnDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): number {
    let count = 0;
    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (shift && shift !== 'REQUESTED_OFF_PLACEHOLDER') {
        count++;
      }
    }
    return count;
  }

  private countOpenersClosersOnDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): {
    openers: number;
    closers: number;
    midShifts: number;
    totalWorkers: number;
  } {
    let openers = 0;
    let closers = 0;
    let midShifts = 0;
    let totalWorkers = 0;

    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (!shift || shift === 'REQUESTED_OFF_PLACEHOLDER') continue;

      // Count total workers
      totalWorkers++;

      try {
        const parts = shift.split(' - ').map((s) => s.trim());
        if (parts.length !== 2) continue;

        const startMinutes = this.parseTime12Hour(parts[0]);
        const endMinutes = this.parseTime12Hour(parts[1]);

        // Opener: starts between 9:00-11:00 AM (but NOT the 11 AM mid shift)
        if (
          startMinutes >= this.OPENING_TIME_START &&
          startMinutes <= this.OPENING_TIME_END &&
          !(startMinutes === 660 && endMinutes === 1140)
        ) {
          openers++;
        }

        // Mid shift: 11:00 AM - 7:00 PM
        if (startMinutes === 660 && endMinutes === 1140) {
          midShifts++;
        }

        // Closer: ends at 10:00 PM
        if (endMinutes === this.CLOSING_TIME) {
          closers++;
        }
      } catch (e) {
        // Skip invalid shifts
      }
    }

    return { openers, closers, midShifts, totalWorkers };
  }

  private assignShift(
    employee: Employee,
    dayName: string,
    shift: ShiftSlot,
    employeeSchedules: Map<string, Map<string, string>>,
    employeeHours: Map<string, number>,
    employeeDaysWorked: Map<string, number>,
    assignedToday: Set<string>,
  ): void {
    const shiftStr = `${this.formatTime(shift.start)} - ${this.formatTime(
      shift.end,
    )}`;
    employeeSchedules.get(employee.name)!.set(dayName, shiftStr);
    assignedToday.add(employee.name);

    const shiftHours = (shift.end - shift.start) / 60;
    employeeHours.set(
      employee.name,
      (employeeHours.get(employee.name) || 0) + shiftHours,
    );
    employeeDaysWorked.set(
      employee.name,
      (employeeDaysWorked.get(employee.name) || 0) + 1,
    );
  }

  private countOpenersOnDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): number {
    let count = 0;
    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (!shift || shift === 'REQUESTED_OFF_PLACEHOLDER') continue;

      try {
        const parts = shift.split(' - ').map((s) => s.trim());
        if (parts.length !== 2) continue;

        const startMinutes = this.parseTime12Hour(parts[0]);
        const endMinutes = this.parseTime12Hour(parts[1]);

        // Opener: starts between 9:00-11:00 AM (but NOT the 11 AM - 7 PM mid shift)
        if (
          startMinutes >= this.OPENING_TIME_START &&
          startMinutes <= this.OPENING_TIME_END &&
          !(startMinutes === 660 && endMinutes === 1140)
        ) {
          count++;
        }
      } catch (e) {
        // Skip invalid shifts
      }
    }
    return count;
  }

  private countClosersOnDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): number {
    let count = 0;
    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (!shift || shift === 'REQUESTED_OFF_PLACEHOLDER') continue;

      try {
        const parts = shift.split(' - ').map((s) => s.trim());
        if (parts.length !== 2) continue;

        const endMinutes = this.parseTime12Hour(parts[1]);
        if (endMinutes === this.CLOSING_TIME) {
          count++;
        }
      } catch (e) {
        // Skip invalid shifts
      }
    }
    return count;
  }

  private buildScheduleMap(
    schedule: ScheduleEntry[],
  ): Map<string, Map<string, string>> {
    const scheduleMap = new Map<string, Map<string, string>>();

    for (const entry of schedule) {
      const dayMap = new Map<string, string>();
      for (const day of this.DAYS) {
        dayMap.set(day, entry[day as keyof ScheduleEntry] as string);
      }
      scheduleMap.set(entry.name, dayMap);
    }

    return scheduleMap;
  }

  private countKeyHoldersOnDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): number {
    let count = 0;

    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (
        !shift ||
        shift.toLowerCase() === 'off' ||
        shift.toLowerCase() === 'requested off'
      ) {
        continue;
      }

      const employee = this.employeeData?.find((emp) => emp.name === empName);
      if (employee?.keyHolder) {
        count++;
      }
    }

    return count;
  }

  private hasMorningKeyHolderCoverage(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): boolean {
    const requiredTimes = [540, 570, 600];

    return requiredTimes.every((requiredMinute) => {
      for (const [empName, empSchedule] of schedules.entries()) {
        const shift = empSchedule.get(dayName);
        if (
          !shift ||
          shift.toLowerCase() === 'off' ||
          shift.toLowerCase() === 'requested off'
        ) {
          continue;
        }

        const employee = this.employeeData?.find((emp) => emp.name === empName);
        if (!employee?.keyHolder) continue;

        try {
          const parts = shift.split(' - ').map((part) => part.trim());
          if (parts.length !== 2) continue;

          const startMinutes = this.parseTime12Hour(parts[0]);
          const endMinutes = this.parseTime12Hour(parts[1]);

          if (startMinutes <= requiredMinute && endMinutes > requiredMinute) {
            return true;
          }
        } catch {
          continue;
        }
      }

      return false;
    });
  }

  private hasKeyHolderOpen9AMAndClose2PM(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): boolean {
    const keyHoldersOnDay: string[] = [];

    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (
        !shift ||
        shift.toLowerCase() === 'off' ||
        shift.toLowerCase() === 'requested off'
      ) {
        continue;
      }

      const employee = this.employeeData?.find((emp) => emp.name === empName);
      if (employee?.keyHolder) {
        keyHoldersOnDay.push(empName);
      }
    }

    // Only enforce if there are >= 2 key holders
    if (keyHoldersOnDay.length < 2) {
      return true;
    }

    let has9AMOpener = false;
    let has2PMCloser = false;

    for (const empName of keyHoldersOnDay) {
      const shift = schedules.get(empName)?.get(dayName);
      if (!shift) continue;

      try {
        const parts = shift.split(' - ').map((s) => s.trim());
        if (parts.length !== 2) continue;

        const startMinutes = this.parseTime12Hour(parts[0]);

        if (startMinutes === 540) {
          has9AMOpener = true;
        }
        if (startMinutes === 840) {
          has2PMCloser = true;
        }
      } catch {
        continue;
      }
    }

    return has9AMOpener && has2PMCloser;
  }

  private hasKeyHolderInOpeningShift(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): boolean {
    // Opening shift: starts between 9:00 AM (540) and 11:00 AM (660)
    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (
        !shift ||
        shift.toLowerCase() === 'off' ||
        shift.toLowerCase() === 'requested off'
      ) {
        continue;
      }

      const employee = this.employeeData?.find((emp) => emp.name === empName);
      if (!employee?.keyHolder) continue;

      try {
        const parts = shift.split(' - ').map((s) => s.trim());
        if (parts.length !== 2) continue;

        const startMinutes = this.parseTime12Hour(parts[0]);
        if (
          startMinutes >= this.OPENING_TIME_START &&
          startMinutes <= this.OPENING_TIME_END
        ) {
          return true;
        }
      } catch {
        continue;
      }
    }

    return false;
  }

  private hasKeyHolderInClosingShift(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): boolean {
    // Closing shift: ends at 10:00 PM (1320)
    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (
        !shift ||
        shift.toLowerCase() === 'off' ||
        shift.toLowerCase() === 'requested off'
      ) {
        continue;
      }

      const employee = this.employeeData?.find((emp) => emp.name === empName);
      if (!employee?.keyHolder) continue;

      try {
        const parts = shift.split(' - ').map((s) => s.trim());
        if (parts.length !== 2) continue;

        const endMinutes = this.parseTime12Hour(parts[1]);
        if (endMinutes === this.CLOSING_TIME) {
          return true;
        }
      } catch {
        continue;
      }
    }

    return false;
  }

  private validateGeneratedSchedule(schedule: ScheduleEntry[]): {
    allRulesPassed: boolean;
    failedRules: string[];
  } {
    const scheduleMap = this.buildScheduleMap(schedule);
    const weekdayDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const failedRules: string[] = [];

    const validations = [
      {
        label: 'Exactly 7 workers per day',
        passed: this.DAYS.every(
          (day) => this.countAssignedOnDay(day, scheduleMap) === 7,
        ),
      },
      {
        label: 'At least 3 openers Monday through Saturday',
        passed: weekdayDays.every(
          (day) => this.countOpenersOnDay(day, scheduleMap) >= 3,
        ),
      },
      {
        label: 'At least 3 closers Monday through Saturday',
        passed: weekdayDays.every(
          (day) => this.countClosersOnDay(day, scheduleMap) >= 3,
        ),
      },
      {
        label: 'Part-time employees stay within the hour limit',
        passed: schedule.every((entry) => {
          const employee = this.employeeData?.find(
            (emp) => emp.name === entry.name,
          );
          return (
            !employee ||
            employee.fullTime ||
            entry.totalHours <= this.PART_TIME_MAX_HOURS
          );
        }),
      },
      {
        label: 'Full-time employees stay within the hour limit',
        passed: schedule.every((entry) => {
          const employee = this.employeeData?.find(
            (emp) => emp.name === entry.name,
          );
          return (
            !employee ||
            !employee.fullTime ||
            entry.totalHours <= this.FULL_TIME_TARGET_HOURS
          );
        }),
      },
      {
        label: 'Sunday has at least 1 key holder',
        passed: this.hasKeyHolderOnDay('Sun', scheduleMap),
      },
      {
        label: 'Weekdays have key holder in opening shift (9AM-11AM start)',
        passed: weekdayDays.every((day) =>
          this.hasKeyHolderInOpeningShift(day, scheduleMap),
        ),
      },
      {
        label: 'Weekdays have key holder in closing shift (10PM end)',
        passed: weekdayDays.every((day) =>
          this.hasKeyHolderInClosingShift(day, scheduleMap),
        ),
      },
      {
        label:
          'If 2+ key holders, one opens at 9:00 AM and one closes at 2:00 PM',
        passed: weekdayDays.every((day) =>
          this.hasKeyHolderOpen9AMAndClose2PM(day, scheduleMap),
        ),
      },
      {
        label: 'Each day has at least 1 cashier scheduled',
        passed: this.DAYS.every((day) =>
          this.hasCashierOnDay(day, scheduleMap),
        ),
      },
      {
        label: 'No employee has 7 days off',
        passed: schedule.every((entry) => entry.daysOff < 7),
      },
    ];

    for (const validation of validations) {
      if (!validation.passed) {
        failedRules.push(validation.label);
      }
    }

    return {
      allRulesPassed: failedRules.length === 0,
      failedRules,
    };
  }

  private createCustomShift(
    emp: Employee,
    dayName: string,
    currentHours: number,
    maxHours: number,
    isSunday: boolean,
  ): ShiftSlot | null {
    const avail = emp.availability[dayName];
    if (!avail || avail.toLowerCase() === 'off') return null;

    const availWindow = this.parseAvailability(avail);
    if (!availWindow) return null;

    const remainingHours = maxHours - currentHours;
    if (remainingHours < 3) return null;

    const availableMinutes = availWindow.end - availWindow.start;
    const availableHours = availableMinutes / 60;

    // CHANGED: Only whole hours now (9, 8, 7, 6, etc. - no decimals)
    let shiftHours = Math.min(
      9,
      Math.floor(availableHours),
      Math.floor(remainingHours),
    );
    if (shiftHours < 3) return null;

    const shiftMinutes = shiftHours * 60; // CHANGED: Now always whole hours

    let start = availWindow.start;
    let end = start + shiftMinutes;

    if (availWindow.end >= this.CLOSING_TIME && !isSunday) {
      end = this.CLOSING_TIME;
      start = end - shiftMinutes;
      if (start < availWindow.start) start = availWindow.start;
    } else if (
      availWindow.start <= this.OPENING_TIME_END &&
      availWindow.start >= this.OPENING_TIME_START &&
      !isSunday
    ) {
      start = availWindow.start;
      end = start + shiftMinutes;
      if (end > availWindow.end) end = availWindow.end;
    } else {
      start = availWindow.start;
      end = start + shiftMinutes;
      if (end > availWindow.end) {
        end = availWindow.end;
        start = end - shiftMinutes;
        if (start < availWindow.start) start = availWindow.start;
      }
    }

    if (end <= start) return null;
    if (start < availWindow.start || end > availWindow.end) return null;

    return {
      start,
      end,
      needsKeyHolder: false,
    };
  }

  private hasKeyHolderOnDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): boolean {
    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (!shift || shift === 'REQUESTED_OFF_PLACEHOLDER') continue;

      const employee = this.employeeData?.find((emp) => emp.name === empName);
      if (employee && employee.keyHolder) {
        return true;
      }
    }
    return false;
  }

  private hasCashierOnDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
  ): boolean {
    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (!shift || shift === 'REQUESTED_OFF_PLACEHOLDER') continue;

      const employee = this.employeeData?.find((emp) => emp.name === empName);
      if (employee?.cashier) {
        return true;
      }
    }
    return false;
  }

  private assignCashierToDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
    employeeHours: Map<string, number>,
    employeeDaysWorked: Map<string, number>,
  ): void {
    const cashiers = this.employeeData!.filter((emp) => emp.cashier);
    const dayMap: { [key: string]: string } = {
      Sun: 'Sunday',
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
    };
    const fullDayName = dayMap[dayName];

    for (const cashier of cashiers) {
      if (schedules.get(cashier.name)!.has(dayName)) continue;

      const hours = employeeHours.get(cashier.name) || 0;
      const maxHours = cashier.fullTime
        ? this.FULL_TIME_TARGET_HOURS
        : this.PART_TIME_MAX_HOURS;

      if (hours >= maxHours - 3) continue;

      const customShift = this.createCustomShift(
        cashier,
        fullDayName,
        hours,
        maxHours,
        dayName === 'Sun',
      );

      if (customShift) {
        const shiftStr = `${this.formatTime(customShift.start)} - ${this.formatTime(
          customShift.end,
        )}`;
        schedules.get(cashier.name)!.set(dayName, shiftStr);
        const shiftHours = (customShift.end - customShift.start) / 60;
        employeeHours.set(cashier.name, hours + shiftHours);
        employeeDaysWorked.set(
          cashier.name,
          (employeeDaysWorked.get(cashier.name) || 0) + 1,
        );

        console.log(
          `Assigned cashier ${cashier.name} to ${dayName} to ensure cashier coverage`,
        );
        return;
      }
    }

    console.warn(`Could not find available cashier for ${dayName} coverage`);
  }

  private hasKeyHolderInShift(
    dayName: string,
    shiftType: 'opener' | 'closer',
    schedules: Map<string, Map<string, string>>,
  ): boolean {
    for (const [empName, empSchedule] of schedules.entries()) {
      const shift = empSchedule.get(dayName);
      if (!shift || shift === 'REQUESTED_OFF_PLACEHOLDER') continue;

      const employee = this.employeeData?.find((emp) => emp.name === empName);
      if (!employee || !employee.keyHolder) continue;

      try {
        const parts = shift.split(' - ').map((s) => s.trim());
        if (parts.length !== 2) continue;

        const startMinutes = this.parseTime12Hour(parts[0]);
        const endMinutes = this.parseTime12Hour(parts[1]);

        if (shiftType === 'opener') {
          // Opener: starts between 9:00-11:00 AM (but NOT the 11 AM - 7 PM mid shift)
          if (
            startMinutes >= this.OPENING_TIME_START &&
            startMinutes <= this.OPENING_TIME_END &&
            !(startMinutes === 660 && endMinutes === 1140)
          ) {
            return true;
          }
        } else if (shiftType === 'closer') {
          // Closer: ends at 10:00 PM
          if (endMinutes === this.CLOSING_TIME) {
            return true;
          }
        }
      } catch (e) {
        // Skip invalid shifts
      }
    }
    return false;
  }

  private assignKeyHolderToDay(
    dayName: string,
    schedules: Map<string, Map<string, string>>,
    employeeHours: Map<string, number>,
    employeeDaysWorked: Map<string, number>,
  ): void {
    const keyHolders = this.employeeData!.filter((emp) => emp.keyHolder);
    const dayMap: { [key: string]: string } = {
      Sun: 'Sunday',
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
    };
    const fullDayName = dayMap[dayName];

    // Find a key holder who can work on this day
    for (const keyHolder of keyHolders) {
      if (schedules.get(keyHolder.name)!.has(dayName)) continue; // Already scheduled

      const hours = employeeHours.get(keyHolder.name) || 0;
      const maxHours = keyHolder.fullTime ? 42 : 28;

      if (hours >= maxHours - 3) continue; // Not enough room for a shift

      const customShift = this.createCustomShift(
        keyHolder,
        fullDayName,
        hours,
        maxHours,
        dayName === 'Sun',
      );

      if (customShift) {
        const shiftStr = `${this.formatTime(customShift.start)} - ${this.formatTime(
          customShift.end,
        )}`;
        schedules.get(keyHolder.name)!.set(dayName, shiftStr);
        const shiftHours = (customShift.end - customShift.start) / 60;
        employeeHours.set(
          keyHolder.name,
          (employeeHours.get(keyHolder.name) || 0) + shiftHours,
        );
        employeeDaysWorked.set(
          keyHolder.name,
          (employeeDaysWorked.get(keyHolder.name) || 0) + 1,
        );
        console.log(
          `Assigned key holder ${keyHolder.name} to ${dayName} to ensure coverage`,
        );
        return;
      }
    }

    console.warn(`Could not find available key holder for ${dayName} coverage`);
  }

  private assignKeyHolderToShift(
    dayName: string,
    shiftType: 'opener' | 'closer',
    schedules: Map<string, Map<string, string>>,
    employeeHours: Map<string, number>,
    employeeDaysWorked: Map<string, number>,
  ): void {
    const keyHolders = this.employeeData!.filter((emp) => emp.keyHolder);
    const dayMap: { [key: string]: string } = {
      Sun: 'Sunday',
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
    };
    const fullDayName = dayMap[dayName];
    const isSunday = dayName === 'Sun';

    const allShifts = this.getShiftSlots(dayName);
    let targetShifts: ShiftSlot[] = [];

    if (shiftType === 'opener') {
      targetShifts = allShifts.filter(
        (s) =>
          s.start >= this.OPENING_TIME_START &&
          s.start <= this.OPENING_TIME_END &&
          !(s.start === 660 && s.end === 1140),
      );
    } else {
      targetShifts = allShifts.filter((s) => s.end === this.CLOSING_TIME);
    }

    for (const shift of targetShifts) {
      for (const keyHolder of keyHolders) {
        const currentSchedule = schedules.get(keyHolder.name)!.get(dayName);
        if (
          currentSchedule &&
          currentSchedule !== 'REQUESTED_OFF_PLACEHOLDER'
        ) {
          continue;
        }

        const hours = employeeHours.get(keyHolder.name) || 0;
        if (!this.canWorkShift(keyHolder, dayName, shift, hours)) {
          continue;
        }

        const shiftStr = `${this.formatTime(shift.start)} - ${this.formatTime(
          shift.end,
        )}`;
        schedules.get(keyHolder.name)!.set(dayName, shiftStr);
        const shiftHours = (shift.end - shift.start) / 60;
        employeeHours.set(
          keyHolder.name,
          (employeeHours.get(keyHolder.name) || 0) + shiftHours,
        );
        employeeDaysWorked.set(
          keyHolder.name,
          (employeeDaysWorked.get(keyHolder.name) || 0) + 1,
        );
        console.log(
          `Assigned key holder ${keyHolder.name} to ${dayName} ${shiftType} shift to ensure coverage`,
        );
        return;
      }
    }

    for (const keyHolder of keyHolders) {
      const currentSchedule = schedules.get(keyHolder.name)!.get(dayName);
      if (currentSchedule && currentSchedule !== 'REQUESTED_OFF_PLACEHOLDER') {
        continue;
      }

      const hours = employeeHours.get(keyHolder.name) || 0;
      const maxHours = keyHolder.fullTime ? 42 : 28;

      if (hours >= maxHours - 3) continue;

      const customShift = this.createCustomShift(
        keyHolder,
        fullDayName,
        hours,
        maxHours,
        isSunday,
      );

      if (customShift) {
        const shiftStr = `${this.formatTime(customShift.start)} - ${this.formatTime(
          customShift.end,
        )}`;
        schedules.get(keyHolder.name)!.set(dayName, shiftStr);
        const shiftHours = (customShift.end - customShift.start) / 60;
        employeeHours.set(
          keyHolder.name,
          (employeeHours.get(keyHolder.name) || 0) + shiftHours,
        );
        employeeDaysWorked.set(
          keyHolder.name,
          (employeeDaysWorked.get(keyHolder.name) || 0) + 1,
        );
        console.log(
          `Assigned key holder ${keyHolder.name} to ${dayName} ${shiftType} shift (custom) to ensure coverage`,
        );
        return;
      }
    }

    console.warn(
      `Could not find available key holder for ${dayName} ${shiftType} shift`,
    );
  }

  getEmployeeAvailability(
    employeeName: string,
    dayOfWeek: string,
  ): string | null {
    const employee = this.employeeData?.find(
      (emp) => emp.name === employeeName,
    );
    if (!employee) return null;

    const dayMap: { [key: string]: string } = {
      Sun: 'Sunday',
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
    };

    const fullDayName = dayMap[dayOfWeek];
    return employee.availability[fullDayName] || null;
  }

  getCellBackgroundColor(
    employeeName: string,
    dayOfWeek: string,
    cellValue: string,
  ): string {
    const availability = this.getEmployeeAvailability(employeeName, dayOfWeek);

    // Find the full employee data to check if they're a key holder
    const fullEmployee = this.employeeData?.find(
      (emp) => emp.name === employeeName,
    );
    const isKeyHolder = fullEmployee?.keyHolder || false;

    // Check if this is a valid shift (not "off" or "requested off")
    if (
      cellValue &&
      cellValue.toLowerCase() !== 'off' &&
      cellValue.toLowerCase() !== 'requested off'
    ) {
      // Key holders always get purple, regardless of shift time
      if (isKeyHolder) {
        return 'rgba(138, 43, 226, 0.25)'; // Purple - key holder shift
      }

      // Regular shift
      return 'rgba(40, 167, 69, 0.15)'; // Green - regular shift
    }

    // If requested off
    if (cellValue && cellValue.toLowerCase() === 'requested off') {
      return 'rgba(255, 193, 7, 0.2)'; // Yellow - requested off
    }

    // If available but no shift
    if (availability && availability.toLowerCase() !== 'off') {
      return 'rgba(13, 110, 253, 0.1)'; // Blue - available
    }

    // If not available
    return 'rgba(220, 53, 69, 0.08)'; // Light red - not available
  }

  getNameColor(employee: ScheduleEntry): string {
    const worked = 7 - employee.daysOff;

    // Priority 1: If no shifts, always show red
    if (worked === 0) {
      return '#dc3545'; // Bootstrap danger - Red
    }

    // Priority 2+: Check employee status flags from employeeData
    const fullEmployee = this.employeeData?.find(
      (emp) => emp.name === employee.name,
    );

    if (fullEmployee) {
      const keyHolder = !!fullEmployee.keyHolder;
      const fullTime = !!fullEmployee.fullTime;
      const cashier = !!fullEmployee.cashier;

      // Purple — key holder (any combination)
      if (keyHolder) {
        return '#ca57de';
      }

      // Gold — full-time + cashier
      if (fullTime && cashier) {
        return '#d4a017';
      }

      // Green — cashier only
      if (cashier && !fullTime) {
        return '#28a745';
      }

      // Blue — full-time only
      if (fullTime && !cashier) {
        return '#0d6efd';
      }
    }

    // Black — default (part-time with no special roles)
    return '#000000';
  }

  getNameClass(employee: ScheduleEntry): string {
    // No longer needed since we're not using gradient
    return '';
  }

  getNameFontWeight(employee: ScheduleEntry): string {
    const worked = 7 - employee.daysOff;

    // Bold for no shifts (to make the red stand out)
    if (worked === 0) {
      return 'bold';
    }

    // Check for keyholder or full-time status
    const fullEmployee = this.employeeData?.find(
      (emp) => emp.name === employee.name,
    );

    if (fullEmployee && (fullEmployee.keyHolder || fullEmployee.fullTime)) {
      return '600'; // Semi-bold for keyholders and full-time
    }

    return 'normal';
  }

  openDayCoverageModal(day: string, dayIndex: number): void {
    if (!this.result?.length) {
      this.selectedCoverageEmployees = [];
      return;
    }

    const dayNameMap: { [key: string]: string } = {
      Sun: 'Sunday',
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
    };

    const selectedDate = this.weekDates[dayIndex] || '';
    const dateLabel = selectedDate
      ? this.parseYMDLocal(selectedDate).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })
      : 'No date set';

    this.selectedCoverageDayHeader = `${dayNameMap[day] || day} — ${dateLabel}`;

    this.selectedCoverageEmployees = this.result
      .map((entry) => {
        const shift = (entry[day as keyof ScheduleEntry] as string) || '';
        const normalizedShift = shift.trim().toLowerCase();

        if (
          !shift ||
          normalizedShift === 'off' ||
          normalizedShift === 'requested off'
        ) {
          return null;
        }

        const employee = this.employeeData?.find(
          (emp) => emp.name === entry.name,
        );

        let startMinutes = Number.MAX_SAFE_INTEGER;
        try {
          const parts = shift.split('-').map((s) => s.trim());
          if (parts.length === 2) {
            startMinutes = this.parseTime12Hour(parts[0]);
          }
        } catch {
          startMinutes = Number.MAX_SAFE_INTEGER;
        }

        return {
          entry,
          shift,
          startMinutes,
          keyHolder: !!employee?.keyHolder,
          fullTime: !!employee?.fullTime,
          cashier: !!employee?.cashier,
        } as DayCoverageEmployeeCard;
      })
      .filter((item): item is DayCoverageEmployeeCard => item !== null)
      .sort((a, b) => a.startMinutes - b.startMinutes);

    const modalEl = document.getElementById('dayCoverageModal');
    if (!modalEl) return;
    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  getAvailabilityDisplay(employee: any, dayName: string): string {
    if (!employee || !employee.id) return 'N/A';

    const fullEmployee = this.employeeData?.find(
      (emp) => emp.name === employee.name,
    );
    if (!fullEmployee || !fullEmployee.availability) return 'N/A';

    const avail = fullEmployee.availability[dayName];
    if (!avail || avail.toLowerCase() === 'off') {
      return 'Off';
    }

    return avail;
  }

  getAvailabilityClass(employee: any, dayName: string): string {
    const avail = this.getAvailabilityDisplay(employee, dayName);

    if (avail === 'Off' || avail === 'N/A') {
      return 'table-danger';
    }

    return 'table-success';
  }

  downloadPDF() {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.showToast('Please allow popups to download PDF', 'error');
      return;
    }

    const printContent = this.generatePrintHTML();
    printWindow.document.write(printContent);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
    };
  }

  generatePrintHTML(): string {
    const currentDate = new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const formatDate = (dateStr: string) => {
      if (!dateStr) return '';

      const [y, m, d] = dateStr.split('-').map(Number);
      const date = new Date(y, (m ?? 1) - 1, d ?? 1);

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    };

    let tableRows = '';
    this.result.forEach((employee) => {
      tableRows += `
        <tr>
          <td style="font-weight: bold;">${employee.name}</td>
          <td>${employee.Sun || 'off'}</td>
          <td>${employee.Mon || 'off'}</td>
          <td>${employee.Tue || 'off'}</td>
          <td>${employee.Wed || 'off'}</td>
          <td>${employee.Thu || 'off'}</td>
          <td>${employee.Fri || 'off'}</td>
          <td>${employee.Sat || 'off'}</td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Weekly Schedule</title>
        <style>
          @media print {
            body { margin: 0; }
            @page { margin: 0.5in; }
          }
          
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }
          
          .header {
            text-align: center;
            margin-bottom: 20px;
          }
          
          h1 {
            margin: 0;
            font-size: 24px;
            color: #333;
          }
          
          .date {
            color: #666;
            font-size: 14px;
            margin-top: 5px;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          
          th {
            background-color: #f8f9fa;
            color: #333;
            padding: 12px 8px;
            text-align: left;
            font-size: 12px;
            border: 1px solid #dee2e6;
            font-weight: bold;
          }
          
          th .date-header {
            display: block;
            font-size: 10px;
            font-weight: normal;
            margin-top: 2px;
          }
          
          td {
            padding: 10px 8px;
            border: 1px solid #dee2e6;
            font-size: 11px;
          }
          
          tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          
          tr:hover {
            background-color: #e9ecef;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Weekly Employee Schedule</h1>
          <div class="date">Generated on ${currentDate}</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Sun<span class="date-header">${formatDate(
                this.weekDates[0],
              )}</span></th>
              <th>Mon<span class="date-header">${formatDate(
                this.weekDates[1],
              )}</span></th>
              <th>Tue<span class="date-header">${formatDate(
                this.weekDates[2],
              )}</span></th>
              <th>Wed<span class="date-header">${formatDate(
                this.weekDates[3],
              )}</span></th>
              <th>Thu<span class="date-header">${formatDate(
                this.weekDates[4],
              )}</span></th>
              <th>Fri<span class="date-header">${formatDate(
                this.weekDates[5],
              )}</span></th>
              <th>Sat<span class="date-header">${formatDate(
                this.weekDates[6],
              )}</span></th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
      </html>
    `;
  }
}
